import * as React from 'react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NumberedBoxProps {
  number: number;
  children: ReactNode;
  helpKey?: string; // Optional help text key for translations
}

export function NumberedBox({ number, children, helpKey }: NumberedBoxProps) {
  const { t } = useTranslation(['boxHelp']);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Find the first h2 element in the children to position the help button next to it
  const findBoxTitle = () => {
    // This is a placeholder - we'll position the button absolutely
    return null;
  };

  return (
    <div className="relative pt-2 pl-2">
      {/* Numbered Badge */}
      <div className="absolute -top-5 -left-5 w-12 h-12 bg-neobrutalism-yellow border-2 border-black rounded-full flex items-center justify-center transform rotate-12 shadow-brutal-sm z-10 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-5"></div>
        <div className="absolute top-0 left-0 w-full h-1/2 bg-white opacity-20"></div>
        <span className="text-2xl font-black text-black relative z-10">{number}</span>
      </div>
      
      {/* Help Button (if helpKey is provided) */}
      {helpKey && (
        <button
          onClick={() => setShowHelpModal(true)}
          className="absolute top-4 right-4 w-7 h-7 bg-neobrutalism-blue border-2 border-black rounded-full flex items-center justify-center shadow-brutal-sm hover:translate-y-[-1px] transition-transform"
          aria-label={t('helpButton')}
        >
          <span className="text-white font-bold text-sm">?</span>
        </button>
      )}
      
      {/* Content */}
      {children}
      
      {/* Help Modal */}
      {showHelpModal && helpKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-neobrutalism max-w-md w-full mx-4 relative">
            <button 
              onClick={() => setShowHelpModal(false)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-neobrutalism-red border-2 border-black rounded-full flex items-center justify-center shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              aria-label={t('closeButton')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-bold text-neobrutalism-text">
                {t('helpTitle')} <span className="text-neobrutalism-accent">{t(`sectionTitle.${helpKey}`)}</span>
              </h2>
            </div>
            
            <p className="text-gray-700">
              {t(helpKey)}
            </p>
          </div>
        </div>
      )}
    </div>
  )
} 